/* The answers a task collected, as a table, and the approve/reject workflow over them.
 *
 * MOUNTED ONLY WHILE OPEN, and that is not negotiable (spec/03#mount-one-section-at-a-time). A task
 * can hold dozens of answers, each with several photos, so rendering them alongside the form would
 * put a request and an unbounded image list on every page open. `Page.js` fetches on first open and
 * caches; switching away unmounts this — not `display: none`, actually unmounted, which is what
 * `document.querySelectorAll(".tk-answers").length` asserts.
 *
 * Built from the SHARED table parts so it reads as the same product as every other table in the
 * app: `PersonCell` / `actionsColumn` / `RowActions` / `dash` from `component/table-cells.js`, the
 * `lp-table__grid` skin from `component/list-page.scss` (header type, body type, hover) and its
 * `lp-main` / `lp-main__sub` / `lp-num` cell classes — all imported once from `src/index.js`.
 *
 * ⚠ `visit/sections.js`'s `SectionTable` is deliberately NOT reused: it is a local function styled
 * from `vd-table` in `visit-detail.scss`, so importing it would mean importing another page's whole
 * stylesheet to get a border and a font size. The verdict logic IS shared from `visit/parts.js`
 * (`statusOf`, `StatusTag`, `postApproval`) — those are CSS-independent, because the pill's shape
 * comes from `.status-tag` in `theme/antd.scss`, which is global.
 *
 * ⚠ Row fields are read through `util/answer-fields.js`, NOT re-derived here. This file had its own
 * `photosOf` reading `photo.photo_url` off what `AnswerListSerializer` flattens to plain URL
 * STRINGS, and its own `row.visit?.user` for the merchandiser, which that serializer lifts to the
 * top level — so a four-photo answer showed "—" and "Answered by" was always empty. It was written
 * against `AnswerSerializer` and never followed the endpoint's switch to the list one.
 */

import React, { useMemo, useState } from "react";
import { Alert as AntAlert, Button, Empty, Image, Skeleton, Space, Table, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import RejectTaskModal from "../../component/RejectTaskModal";
import { PersonCell, actionsColumn, dash } from "../../component/table-cells";
import { TASK_DETAIL } from "../../config/lang";
import { merchandiserOf, photosOf } from "../../util/answer-fields";
import { translate } from "../../util/util";
import { StatusTag, postApproval, statusOf } from "../visit/parts";

export default function Answers({ task, rows, lang, dateFmt, onModerated }) {
  const t = key => translate(TASK_DETAIL, key, lang);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  /* Approve only. Rejecting goes through `RejectTaskModal`, which posts its own `approvals/` row
     after creating the corrective task — the ordering that keeps a rejection from ever existing
     with nothing attached to it. */
  const approve = answer => {
    setBusy(answer.id);
    postApproval({ objectId: answer.id, objectType: "answer", isApproved: true, comment: "" }, ok => {
      setBusy(null);
      if (!ok) {
        Alert.error(t("load_error"), { position: "top-right" });
        return;
      }
      Alert.success(t("approved_msg"), { position: "top-right" });
      /* Only this section and the task's own status refetch — anything that repaints the whole page
         loses the reviewer's place. */
      onModerated();
    });
  };

  const columns = useMemo(
    () => [
      /* Written out rather than built from `statusColumn()`: that helper is shaped for a 44px
         icon-only glyph and this column wants a labelled pill — a reviewer is deciding about the
         word, and there is room for it inside one task's answers. Overriding four of its six
         properties to claim reuse would be worse than not using it. The key and `fixed: "left"`
         match it so the column sits where every other table's status sits. */
      {
        key: "status",
        title: t("col_status"),
        width: 150,
        fixed: "left",
        render: (value, row) => <StatusTag status={statusOf(row.approval)} lang={lang} />,
      },
      {
        key: "photos",
        title: t("k_photos"),
        width: 90,
        render: (value, row) => {
          const photos = photosOf(row);
          if (!photos.length) return dash;
          /* One thumbnail opens the whole set — antd's preview is the viewer, which is also what
             replaced `react-image-lightbox`, whose prev and next both stepped the same way. */
          return (
            <Image.PreviewGroup>
              <Image src={photos[0]} width={44} height={44} className="tk-thumb" alt="" />
              {photos.slice(1).map(url => (
                <Image key={url} src={url} style={{ display: "none" }} alt="" />
              ))}
              {photos.length > 1 ? <span className="tk-thumb__n lp-num">{photos.length}</span> : null}
            </Image.PreviewGroup>
          );
        },
      },
      /* Comment and tags are two columns, not one stacked cell — the same split `/photo-review`
         shows, so a reviewer comparing the two screens reads the same shape twice. */
      {
        key: "answer",
        title: t("ans_comment"),
        ellipsis: true,
        render: (value, row) => (row.answer ? <Typography.Text ellipsis={{ tooltip: row.answer }}>{row.answer}</Typography.Text> : dash),
      },
      {
        key: "tags",
        title: t("cmp_tags"),
        width: 200,
        render: (value, row) => {
          const tags = row.selected_tags || [];
          if (!tags.length) return dash;
          return (
            <Space size={4} wrap>
              {tags.map((tag, index) => (
                <Tag key={`${tag}-${index}`} className="tk-tag--quiet">
                  {tag}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        key: "boolean",
        title: t("cmp_boolean"),
        width: 90,
        /* A yes/no answer is a word, never a colour alone. */
        render: (value, row) =>
          row.boolean_answer === null || row.boolean_answer === undefined ? (
            dash
          ) : (
            <Typography.Text strong>{t(row.boolean_answer ? "ans_yes" : "ans_no")}</Typography.Text>
          ),
      },
      { key: "by", title: t("ans_by"), width: 180, ellipsis: true, render: (value, row) => <PersonCell user={merchandiserOf(row)} strong={false} /> },
      { key: "created", title: t("ans_date"), width: 170, render: (value, row) => (row.created ? dateFmt.format(new Date(row.created)) : dash) },
      /* The verdict is the reason this table exists, so it is NOT behind a `⋮`: the two buttons are
         the row's payload, not an incidental row action. (Every other table in the app keeps the
         menu — there the action is incidental to reading the row.) Offered only while the answer is
         still awaiting a verdict; re-deciding is a different operation, and the old page's menu
         offered Approve and Reject on every row regardless of what had already been decided.
         Plain antd buttons: the green `.vd-btn--ok` lives in `visit-detail.scss`, which is not
         globally imported, so using it here would drag another page's stylesheet in. */
      {
        ...actionsColumn((value, row) =>
          statusOf(row.approval) !== "pending" ? null : (
            <Space size={4}>
              <Button size="small" icon={<CheckCircleOutlined />} disabled={busy !== null} onClick={() => approve(row)}>
                {t("approve")}
              </Button>
              <Button size="small" danger icon={<CloseCircleOutlined />} disabled={busy !== null} onClick={() => setRejecting(row)}>
                {t("reject")}
              </Button>
            </Space>
          )
        ),
        width: 210,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, dateFmt, busy]
  );

  /* `undefined` is "not fetched yet", `null` is "the fetch failed" — different states needing
     different UI (spec/03). An empty array is a real, successful, empty answer list. */
  if (rows === undefined) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (rows === null) {
    return (
      <AntAlert
        type="error"
        showIcon
        message={t("load_error")}
        action={
          <Button size="small" onClick={() => onModerated()}>
            {t("retry")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="tk-answers">
      <Table
        className="lp-table__grid tk-anstable"
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1240 }}
        loading={busy !== null}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("ans_empty")} /> }}
      />

      {/* Rejecting does not just record a verdict, it asks the merchandiser to fix the shelf — the
          same corrective-task flow `/photo-review` and `/answers` use, restored here on PICSELL-1572.
          The modal posts the rejection itself, after the task, so a failed task never leaves a
          rejection with nothing attached. */}
      {rejecting ? (
        <RejectTaskModal
          target={{ objectId: rejecting.id, objectType: "answer" }}
          photo={photosOf(rejecting)[0] || null}
          point={task?.point || null}
          visitId={rejecting.visit_id}
          lang={lang}
          onCancel={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            onModerated();
          }}
        />
      ) : null}
    </div>
  );
}
